<template>
  <div class="image-slider">
    <div
      class="arrow leftArrow"
      v-if="product.images.length > 1"
      @click="changeImage(-1)"
    >
      <svg class="compactImg differentColour">
        <use href="@/assets/icons/iconset.svg#arrowleft_carousel_smaller"></use>
      </svg>
    </div>
    <div class="image-container">
      <template v-if="product.images && product.images.length > 0">
        <img
          class="productImage"
          v-for="(image, index) in product.images"
          :key="index"
          :src="image"
          alt="Product Image"
          v-show="currentImageIndex === index"
        />
      </template>
      <div v-else class="placeholderImage">
        <div class="placeholderContent">
          <div class="placeholderTitle">
            {{ product.itemName || product.title || "No image available" }}
          </div>
          <div class="placeholderSubtitle">No image available</div>
        </div>
      </div>
    </div>
    <div
      class="arrow rightArrow"
      v-if="product.images.length > 1"
      @click="changeImage(1)"
    >
      <svg class="compactImg differentColour">
        <use
          href="@/assets/icons/iconset.svg#arrowright_carousel_smaller"
        ></use>
      </svg>
    </div>

    <!-- Image Indicators -->
    <div class="image-indicators" v-if="product.images.length > 1">
      <div
        v-for="(image, index) in product.images"
        :key="index"
        :class="['circleInd', { active: currentImageIndex === index }]"
      ></div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";

export default {
  name: "ProductCarousel",
  props: {
    product: {
      type: Object,
      required: true,
    },
  },
  setup(props) {
    // Always initialize with 0 since our strategy is to move the thumbnail to position 0
    const currentImageIndex = ref(0);

    // Log info about the product for debugging
    console.log(
      `[ProductCarousel] Initializing with ${
        props.product.images?.length || 0
      } images`
    );

    const changeImage = (direction) => {
      console.log(`[ProductCarousel] Changing image, direction: ${direction}`);

      if (props.product.images && props.product.images.length > 0) {
        // Calculate the new index with proper wrapping
        currentImageIndex.value =
          (currentImageIndex.value + direction + props.product.images.length) %
          props.product.images.length;

        console.log(
          `[ProductCarousel] New image index: ${currentImageIndex.value}`
        );
      }
    };

    return {
      currentImageIndex,
      changeImage,
    };
  },
};
</script>

<style scoped>
.image-slider {
  position: relative;
  width: 100% !important;
  border-radius: var(--radius-sm);
  /* max-height: 70vh; */
  border: 1px solid var(--color-surface-raised);
  box-shadow: var(--shadow-sticker);
}
.image-container {
  z-index: 1;
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 Aspect Ratio */
  overflow: hidden; /* If you want text inside of the container to resize on font change, set to 'auto' */
}

.productImage {
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  right: 0;
  object-fit: contain;
  width: 100%;
  height: 100%;
}

.placeholderImage {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-surface-raised);
}

.placeholderContent {
  text-align: center;
  padding: var(--space-7);
}

.placeholderTitle {
  font-family: var(--font-family-ui);
  font-size: var(--font-size-display);
  line-height: 1.05;
}

.placeholderSubtitle {
  margin-top: var(--space-4);
  opacity: 0.7;
  font-size: var(--font-size-body);
}
.arrow {
  z-index: 2;
  position: absolute;
  opacity: 0.8;
  transition: transform 0.15s;
  height: 100%;
  display: flex;
  align-items: center;
  bottom: 4px;
}

.arrow svg {
  filter: drop-shadow(0px 0px 0.7px rgb(255 255 255)) !important;
}

.arrow:hover {
  transform: translateY(var(--motion-pressed-offset));
}

.arrow {
  mix-blend-mode: difference;
}

.arrow.rightArrow {
  cursor: e-resize;
  right: 0;
}
.arrow.leftArrow {
  cursor: w-resize;
  left: 0;
}

.image-indicators {
  z-index: 2;
  position: absolute;
  bottom: 9px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: var(--space-1);
}

.circleInd {
  width: 7px;
  height: 7px;
  background-color: var(--color-media-indicator);
  border-radius: 50%;
  opacity: 1;
  border: 1px solid var(--color-text-primary);
  transition: width var(--motion-standard) ease-in,
    height var(--motion-standard) ease-in;
}

.circleInd.active {
  width: 9px;
  height: 9px;
  opacity: 1;
}
@media only screen and (max-width: 490px) {
  .image-container {
    padding-bottom: calc(100% + var(--space-5));
  }
}
</style>
